import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Copy, Printer, Scale, Calculator, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Helmet } from "react-helmet-async";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

type HeadType = "flat" | "elliptical" | "asme-flanged-dished" | "standard-flanged-dished" | "80-10-flanged-dished" | "hemispherical" | "conical-10" | "conical-15";
type TankOrientation = "vertical" | "horizontal";

interface MaterialOption {
  label: string;
  value: string;
  density: number;
  description: string;
}

interface CalculationResult {
  headTopVolL: string;
  headBottomVolL: string;
  shellVolL: string;
  totalVolL: string;
  totalVolM3: string;
  estimatedWeightKg: string;
  loadPerLegKg: string;
  material: string;
  safetyFactor: number;
}

const TankWeightCalculator = () => {
  
  // Material selection
  const materialOptions: MaterialOption[] = [
    { 
      label: "Mild Steel", 
      value: "mild_steel", 
      density: 7850,
      description: "Standard carbon steel for general applications"
    },
    { 
      label: "Carbon Steel", 
      value: "carbon_steel", 
      density: 7850,
      description: "High-strength carbon steel for industrial use"
    },
    { 
      label: "Stainless Steel 304", 
      value: "ss304", 
      density: 8000,
      description: "General-purpose stainless steel with good corrosion resistance"
    },
    { 
      label: "Stainless Steel 316/316L", 
      value: "ss316", 
      density: 8000,
      description: "Superior corrosion resistance, ideal for pharmaceutical and food applications"
    },
    { 
      label: "Stainless Steel 316Ti", 
      value: "ss316ti", 
      density: 8000,
      description: "Titanium-stabilized grade with excellent high-temperature properties"
    },
    { 
      label: "Duplex Stainless Steel", 
      value: "duplex", 
      density: 7800,
      description: "High-strength stainless steel for demanding applications"
    },
    { 
      label: "Hastelloy C-276", 
      value: "hastelloy", 
      density: 8900,
      description: "Premium nickel-based alloy for extreme corrosion resistance"
    }
  ];

  const headTypes = [
    { value: "flat", label: "Flat Head", description: "Simple flat closure, lowest cost option" },
    { value: "elliptical", label: "Elliptical Head (2:1)", description: "Semi-ellipsoidal head with 2:1 ratio (Height = D/4)" },
    { value: "asme-flanged-dished", label: "ASME Flanged & Dished", description: "Dish Radius = D, Knuckle Radius = 0.06D (Standard for pressure vessels)" },
    { value: "standard-flanged-dished", label: "Standard Flanged & Dished", description: "Dish Radius = D, Knuckle Radius = 0.10D (Common in storage tanks)" },
    { value: "80-10-flanged-dished", label: "80:10 Flanged & Dished", description: "Dish Radius = 0.8D, Knuckle Radius = 0.10D (Shallower profile)" },
    { value: "hemispherical", label: "Hemispherical Head", description: "Perfect hemisphere - maximum strength for high pressures" },
    { value: "conical-10", label: "10° Conical Head", description: "Shallow cone, 10° slope from horizontal (typical for silo/hopper bottoms)" },
    { value: "conical-15", label: "15° Conical Head", description: "Shallow cone, 15° slope from horizontal (typical for silo/hopper bottoms)" }
  ];

  // Tank configuration
  const [material, setMaterial] = useState<string>("ss316");
  const [diameter, setDiameter] = useState<string>("");
  const [shellHeight, setShellHeight] = useState<string>("");
  const [shellThickness, setShellThickness] = useState<string>("4");
  const [headThickness, setHeadThickness] = useState<string>("4");
  const [topHeadType, setTopHeadType] = useState<HeadType>("elliptical");
  const [bottomHeadType, setBottomHeadType] = useState<HeadType>("elliptical");
  const [tankOrientation, setTankOrientation] = useState<TankOrientation>("vertical");
  const [numberOfLegs, setNumberOfLegs] = useState<string>("4");
  const [safetyFactor, setSafetyFactor] = useState<string>("1.2");

  const [results, setResults] = useState<CalculationResult>({
    headTopVolL: "0.00",
    headBottomVolL: "0.00", 
    shellVolL: "0.00",
    totalVolL: "0.00",
    totalVolM3: "0.0000",
    estimatedWeightKg: "0.00",
    loadPerLegKg: "0.00",
    material: "",
    safetyFactor: 1.2
  });

  const { toast } = useToast();

  const getDensity = (materialValue: string): number => {
    const selectedMaterial = materialOptions.find(m => m.value === materialValue);
    return selectedMaterial?.density || 8000;
  };

  // Surface area calculations for steel weight
  const shellSurfaceArea = (diameter: number, height: number): number => {
    return Math.PI * diameter * height; // mm²
  };

  const headSurfaceArea = (diameter: number, shape: HeadType): number => {
    const radius = diameter / 2;
    switch (shape) {
      case "flat":
        return Math.PI * radius * radius; // mm²
      case "elliptical":
        return 1.6 * Math.PI * radius * radius; // 2:1 elliptical approximation
      case "asme-flanged-dished":
      case "standard-flanged-dished":
      case "80-10-flanged-dished":
        return 1.08 * Math.PI * radius * radius; // Torispherical approximation
      case "hemispherical":
        return 2 * Math.PI * radius * radius; // Hemisphere
      case "conical-10": {
        // Lateral cone surface: A = π × r² / cos(β), β from horizontal
        const beta = (10 * Math.PI) / 180;
        return (Math.PI * radius * radius) / Math.cos(beta); // mm²
      }
      case "conical-15": {
        const beta = (15 * Math.PI) / 180;
        return (Math.PI * radius * radius) / Math.cos(beta); // mm²
      }
      default:
        return 0;
    }
  };

  // Volume calculations for tank capacity (internal volume)
  const headVolume = (diameter: number, shape: HeadType): number => {
    const radius = diameter / 2;
    
    switch (shape) {
      case "asme-flanged-dished":
        const asmeR = 0.06 * diameter;
        const asmeRa = diameter;
        const asmeH = asmeRa - Math.sqrt(asmeRa * asmeRa - Math.pow(radius - asmeR, 2)) + asmeR;
        return (Math.PI / 6) * asmeH * (3 * radius * radius + asmeH * asmeH) / 1e9; // Convert to m³
        
      case "standard-flanged-dished":
        const stdR = 0.10 * diameter;
        const stdRa = diameter;
        const stdH = stdRa - Math.sqrt(stdRa * stdRa - Math.pow(radius - stdR, 2)) + stdR;
        return (Math.PI / 6) * stdH * (3 * radius * radius + stdH * stdH) / 1e9;
        
      case "80-10-flanged-dished":
        const fd8010R = 0.10 * diameter;
        const fd8010Ra = 0.8 * diameter;
        const fd8010H = fd8010Ra - Math.sqrt(fd8010Ra * fd8010Ra - Math.pow(radius - fd8010R, 2)) + fd8010R;
        return (Math.PI / 6) * fd8010H * (3 * radius * radius + fd8010H * fd8010H) / 1e9;
        
      case "elliptical":
        const ellipticalHeight = diameter / 4;
        return (Math.PI / 6) * ellipticalHeight * (3 * radius * radius + ellipticalHeight * ellipticalHeight) / 1e9;
        
      case "hemispherical":
        return ((2/3) * Math.PI * Math.pow(radius, 3)) / 1e9;
        
      case "conical-10": {
        // V = (1/3) × π × r³ × tan(β), β from horizontal, convert mm³ → m³
        const beta = (10 * Math.PI) / 180;
        return ((1 / 3) * Math.PI * Math.pow(radius, 3) * Math.tan(beta)) / 1e9;
      }

      case "conical-15": {
        const beta = (15 * Math.PI) / 180;
        return ((1 / 3) * Math.PI * Math.pow(radius, 3) * Math.tan(beta)) / 1e9;
      }

      case "flat":
        return 0; // Flat head adds no internal volume
        
      default:
        return 0;
    }
  };

  const shellVolume = (diameter: number, height: number): number => {
    const radius = diameter / 2;
    return (Math.PI * Math.pow(radius, 2) * height) / 1e9; // m³
  };

  // Steel weight calculation based on surface areas and thickness
  const calculateSteelWeight = (
    diameter: number, 
    height: number, 
    shellThickness: number,
    headThickness: number,
    topHeadType: HeadType, 
    bottomHeadType: HeadType,
    materialDensity: number
  ): number => {
    // Convert thickness from mm to m
    const shellThicknessM = shellThickness / 1000;
    const headThicknessM = headThickness / 1000;
    
    // Calculate surface areas in mm²
    const shellArea = shellSurfaceArea(diameter, height);
    const topHeadArea = headSurfaceArea(diameter, topHeadType);
    const bottomHeadArea = headSurfaceArea(diameter, bottomHeadType);
    
    // Convert surface areas to m²
    const shellAreaM2 = shellArea / 1e6;
    const topHeadAreaM2 = topHeadArea / 1e6;
    const bottomHeadAreaM2 = bottomHeadArea / 1e6;
    
    // Calculate steel volumes in m³
    const shellSteelVol = shellAreaM2 * shellThicknessM;
    const topHeadSteelVol = topHeadAreaM2 * headThicknessM;
    const bottomHeadSteelVol = bottomHeadAreaM2 * headThicknessM;
    
    const totalSteelVol = shellSteelVol + topHeadSteelVol + bottomHeadSteelVol;
    
    // Calculate weight in kg
    return totalSteelVol * materialDensity;
  };

  const calculateTankWeight = () => {
    const dia = parseFloat(diameter);
    const height = parseFloat(shellHeight);
    const shellThick = parseFloat(shellThickness);
    const headThick = parseFloat(headThickness);
    const legs = parseInt(numberOfLegs);
    const safety = parseFloat(safetyFactor);

    if (!dia || !height || !shellThick || !headThick || !legs || !safety) {
      setResults({
        headTopVolL: "0.00",
        headBottomVolL: "0.00", 
        shellVolL: "0.00",
        totalVolL: "0.00",
        totalVolM3: "0.0000",
        estimatedWeightKg: "0.00",
        loadPerLegKg: "0.00",
        material: materialOptions.find(m => m.value === material)?.label || "",
        safetyFactor: safety
      });
      return;
    }

    const density = getDensity(material);
    
    // Calculate tank volume (for capacity information)
    const topVol = headVolume(dia, topHeadType);
    const bottomVol = headVolume(dia, bottomHeadType);
    const shellVol = shellVolume(dia, height);
    const totalVol = topVol + bottomVol + shellVol;
    
    // Debug console logging
    console.log('=== Tank Weight Calculation Debug ===');
    console.log('Input Diameter (mm):', dia);
    console.log('Input Shell Height (mm):', height);
    console.log('Shell Volume (mm³):', shellVol * 1e9);
    console.log('Shell Volume (L):', shellVol * 1000);
    console.log('Top Head Type:', topHeadType);
    console.log('Top Head Volume (L):', topVol * 1000);
    console.log('Bottom Head Type:', bottomHeadType);
    console.log('Bottom Head Volume (L):', bottomVol * 1000);
    console.log('Total Volume (L):', totalVol * 1000);
    console.log('====================================');
    
    // Calculate steel weight (corrected calculation)
    const weightKg = calculateSteelWeight(dia, height, shellThick, headThick, topHeadType, bottomHeadType, density);
    const loadPerLeg = (weightKg * safety) / legs;

    setResults({
      headTopVolL: (topVol * 1000).toFixed(2),
      headBottomVolL: (bottomVol * 1000).toFixed(2),
      shellVolL: (shellVol * 1000).toFixed(2),
      totalVolL: (totalVol * 1000).toFixed(2),
      totalVolM3: totalVol.toFixed(4),
      estimatedWeightKg: weightKg.toFixed(2),
      loadPerLegKg: loadPerLeg.toFixed(2),
      material: materialOptions.find(m => m.value === material)?.label || "",
      safetyFactor: safety
    });
  };

  useEffect(() => {
    calculateTankWeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [material, diameter, shellHeight, shellThickness, headThickness, topHeadType, bottomHeadType, numberOfLegs, safetyFactor]);

  const copyResults = () => {
    const resultText = `Tank Weight Calculator Results:
Material: ${results.material}
Diameter: ${diameter} mm
Shell Height: ${shellHeight} mm
Shell Thickness: ${shellThickness} mm
Head Thickness: ${headThickness} mm
Top Head: ${headTypes.find(h => h.value === topHeadType)?.label}
Bottom Head: ${headTypes.find(h => h.value === bottomHeadType)?.label}
Tank Orientation: ${tankOrientation}
Number of Legs: ${numberOfLegs}
Safety Factor: ${safetyFactor}

Volume Results:
- Top Head Volume: ${results.headTopVolL} L
- Bottom Head Volume: ${results.headBottomVolL} L  
- Shell Volume: ${results.shellVolL} L
- Total Volume: ${results.totalVolL} L (${results.totalVolM3} m³)

Weight Results:
- Estimated Weight: ${results.estimatedWeightKg} kg
- Load per Leg: ${results.loadPerLegKg} kg/leg`;

    navigator.clipboard.writeText(resultText);
    toast({
      title: "Results Copied",
      description: "Tank calculation results have been copied to clipboard",
    });
  };

  const printResults = () => {
    window.print();
  };

  const getHeadTypeDiagram = (type: HeadType) => {
    const diagrams = {
      "flat": (
        <svg viewBox="0 0 100 60" className="w-full h-8">
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
          <line x1="15" y1="50" x2="15" y2="45" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="45" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "elliptical": (
        <svg viewBox="0 0 100 60" className="w-full h-8">
          <ellipse cx="50" cy="50" rx="35" ry="17.5" fill="none" stroke="currentColor" strokeWidth="2" 
            clipPath="polygon(0 50%, 100% 50%, 100% 0%, 0% 0%)"/>
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "asme-flanged-dished": (
        <svg viewBox="0 0 100 60" className="w-full h-8">
          <line x1="15" y1="50" x2="15" y2="35" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="35" stroke="currentColor" strokeWidth="2"/>
          <path d="M15 35 Q20 30 25 28 Q50 22 75 28 Q80 30 85 35" fill="none" stroke="currentColor" strokeWidth="2"/>
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "standard-flanged-dished": (
        <svg viewBox="0 0 100 60" className="w-full h-8">
          <line x1="15" y1="50" x2="15" y2="37" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="37" stroke="currentColor" strokeWidth="2"/>
          <path d="M15 37 Q22 32 28 30 Q50 25 72 30 Q78 32 85 37" fill="none" stroke="currentColor" strokeWidth="2"/>
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "80-10-flanged-dished": (
        <svg viewBox="0 0 100 60" className="w-full h-8">
          <line x1="15" y1="50" x2="15" y2="38" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="38" stroke="currentColor" strokeWidth="2"/>
          <path d="M15 38 Q25 33 30 31 Q50 26 70 31 Q75 33 85 38" fill="none" stroke="currentColor" strokeWidth="2"/>
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "hemispherical": (
        <svg viewBox="0 0 100 60" className="w-full h-8">
          <path d="M15 50 A 35 35 0 0 1 85 50" fill="none" stroke="currentColor" strokeWidth="2"/>
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "conical-10": (
        <svg viewBox="0 0 100 60" className="w-full h-8">
          <line x1="15" y1="50" x2="15" y2="44" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="44" stroke="currentColor" strokeWidth="2"/>
          <path d="M15 44 L50 38 L85 44" fill="none" stroke="currentColor" strokeWidth="2"/>
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "conical-15": (
        <svg viewBox="0 0 100 60" className="w-full h-8">
          <line x1="15" y1="50" x2="15" y2="42" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="42" stroke="currentColor" strokeWidth="2"/>
          <path d="M15 42 L50 33 L85 42" fill="none" stroke="currentColor" strokeWidth="2"/>
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    };
    return diagrams[type] || null;
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-page-modern">
        <Helmet>
          <title>Tank Weight Calculator Malaysia | Stainless Steel Tank Fabrication | WLE Group</title>
          <meta name="description" content="Professional tank weight calculator for SS316L, carbon steel, and specialty alloys. Calculate load distribution and structural requirements for Malaysian tank fabrication projects." />
          <meta name="keywords" content="tank weight calculator, stainless steel tank weight, SS316L calculator, tank fabrication Malaysia, pressure vessel weight, ASME tank design" />
          
          {/* OpenGraph meta tags */}
          <meta property="og:title" content="Tank Weight Calculator Malaysia | Stainless Steel Tank Fabrication | WLE Group" />
          <meta property="og:description" content="Professional tank weight calculator for SS316L, carbon steel, and specialty alloys. Calculate load distribution and structural requirements for Malaysian tank fabrication projects." />
          <meta property="og:url" content={`${window.location.origin}/tools/tank-weight-calculator`} />
          <meta property="og:locale" content="en_MY" />
        </Helmet>
        
        <Navigation />
        
        {/* Hero Section */}
        <section className="py-24">
          <div className="container">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-6">
                <Scale className="h-12 w-12 text-wle-red" />
                <h1 className="text-5xl font-bold text-foreground">
                  Tank Weight Calculator
                </h1>
              </div>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Professional tank weight calculation for structural analysis and foundation design. Calculate material weight, load distribution, and safety factors for various tank configurations and materials.
              </p>
            </div>
          </div>
        </section>

        {/* Calculator Section */}
        <section className="py-16 bg-subtle-texture">
          <div className="container max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Input Card */}
              <Card className="h-fit">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    Tank Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Material Selection */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Material Selection</Label>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Select the tank material. Density values are used for weight calculations.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Select value={material} onValueChange={setMaterial}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1 text-xs font-medium text-muted-foreground border-b">Carbon & Mild Steels</div>
                        {materialOptions.slice(0, 2).map((mat) => (
                          <SelectItem key={mat.value} value={mat.value}>
                            <div className="flex flex-col">
                              <span>{mat.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {mat.density} kg/m³
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                        <div className="px-2 py-1 text-xs font-medium text-muted-foreground border-b">Stainless Steels</div>
                        {materialOptions.slice(2, 5).map((mat) => (
                          <SelectItem key={mat.value} value={mat.value}>
                            <div className="flex flex-col">
                              <span>{mat.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {mat.density} kg/m³
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                        <div className="px-2 py-1 text-xs font-medium text-muted-foreground border-b">Specialty Alloys</div>
                        {materialOptions.slice(5).map((mat) => (
                          <SelectItem key={mat.value} value={mat.value}>
                            <div className="flex flex-col">
                              <span>{mat.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {mat.density} kg/m³
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {material && (
                      <p className="text-xs text-muted-foreground">
                        {materialOptions.find(m => m.value === material)?.description}
                      </p>
                    )}
                  </div>

                  {/* Tank Dimensions - 3 column layout */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="diameter">Diameter (mm)</Label>
                      <Input
                        id="diameter"
                        type="number"
                        placeholder="e.g. 1000"
                        value={diameter}
                        onChange={(e) => setDiameter(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="shell-height">Shell Height (mm)</Label>
                      <Input
                        id="shell-height"
                        type="number"
                        placeholder="e.g. 2000"
                        value={shellHeight}
                        onChange={(e) => setShellHeight(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="shell-thickness">Shell Thickness (mm)</Label>
                      <Input
                        id="shell-thickness"
                        type="number"
                        placeholder="4"
                        value={shellThickness}
                        onChange={(e) => setShellThickness(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Dish Head Thickness */}
                  <div className="space-y-2">
                    <Label htmlFor="head-thickness">Dish Head Thickness (mm)</Label>
                    <Input
                      id="head-thickness"
                      type="number"
                      placeholder="4"
                      value={headThickness}
                      onChange={(e) => setHeadThickness(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Applied to both top and bottom heads
                    </p>
                  </div>

                  {/* Head Types */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Top Head Type</Label>
                      <Select value={topHeadType} onValueChange={(value) => setTopHeadType(value as HeadType)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {headTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-6">
                                  {getHeadTypeDiagram(type.value as HeadType)}
                                </div>
                                <span>{type.label}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Bottom Head Type</Label>
                      <Select value={bottomHeadType} onValueChange={(value) => setBottomHeadType(value as HeadType)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {headTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-6">
                                  {getHeadTypeDiagram(type.value as HeadType)}
                                </div>
                                <span>{type.label}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Tank Configuration */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Tank Orientation</Label>
                      <Select value={tankOrientation} onValueChange={(value) => setTankOrientation(value as TankOrientation)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vertical">Vertical</SelectItem>
                          <SelectItem value="horizontal">Horizontal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="legs">Number of Legs</Label>
                      <Input
                        id="legs"
                        type="number"
                        placeholder="4"
                        value={numberOfLegs}
                        onChange={(e) => setNumberOfLegs(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="safety">Safety Factor</Label>
                      <Input
                        id="safety"
                        type="number"
                        step="0.1"
                        placeholder="1.2"
                        value={safetyFactor}
                        onChange={(e) => setSafetyFactor(e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Results Card */}
              <Card className="h-fit">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="h-5 w-5" />
                    Calculation Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Volume Results */}
                  <div>
                    <h4 className="font-medium mb-3 text-muted-foreground">Volume Results</h4>
                    <div className="grid gap-3">
                      <div className="flex justify-between items-center p-3 bg-accent rounded-lg">
                        <span className="text-sm">Top Head Volume:</span>
                        <span className="font-bold text-wle-blue">{results.headTopVolL} L</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-accent rounded-lg">
                        <span className="text-sm">Bottom Head Volume:</span>
                        <span className="font-bold text-wle-blue">{results.headBottomVolL} L</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-accent rounded-lg">
                        <span className="text-sm">Shell Volume:</span>
                        <span className="font-bold text-wle-blue">{results.shellVolL} L</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-wle-blue/10 border border-wle-blue/20 rounded-lg">
                        <span className="font-medium">Total Volume:</span>
                        <div className="text-right">
                          <div className="text-lg font-bold text-wle-blue">{results.totalVolL} L</div>
                          <div className="text-sm text-muted-foreground">{results.totalVolM3} m³</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Weight Results */}
                  <div>
                    <h4 className="font-medium mb-3 text-muted-foreground">Weight Results</h4>
                    <div className="grid gap-3">
                      <div className="flex justify-between items-center p-4 bg-wle-red/10 border border-wle-red/20 rounded-lg">
                        <span className="font-medium">Estimated Weight:</span>
                        <div className="text-right">
                          <div className="text-lg font-bold text-wle-red">{results.estimatedWeightKg} kg</div>
                          <div className="text-sm text-muted-foreground">
                            {(parseFloat(results.estimatedWeightKg) / 1000).toFixed(2)} tons
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-accent rounded-lg">
                        <span className="font-medium">Load per Leg:</span>
                        <span className="text-lg font-bold text-wle-red">{results.loadPerLegKg} kg/leg</span>
                      </div>
                    </div>
                  </div>

                  {/* Material & Settings Summary */}
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-medium mb-2">Calculation Parameters</h4>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div>Material: {results.material}</div>
                      <div>Safety Factor: {results.safetyFactor}</div>
                      <div>Number of Legs: {numberOfLegs}</div>
                      <div>Density: {getDensity(material)} kg/m³</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button onClick={copyResults} variant="outline" className="flex-1">
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Results
                    </Button>
                    <Button onClick={printResults} variant="outline" className="flex-1">
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Engineering Disclaimer */}
        <section className="py-12 bg-muted/50">
          <div className="container max-w-4xl">
            <Card className="border-wle-blue/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Info className="h-6 w-6 text-wle-blue flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">🔍 Engineering Disclaimer</h3>
                    <p className="text-muted-foreground mb-3">
                      These tools provide <strong>accurate preliminary calculations</strong> for design and planning purposes. 
                      The formulas used are based on industry-standard engineering practices and ASME standards.
                    </p>
                    <p className="text-muted-foreground mb-3">
                      For <strong>final specifications, detailed engineering analysis, and professional certifications</strong>, 
                      we strongly recommend consulting with our experienced engineering team.
                    </p>
                    <div className="p-3 bg-wle-red/10 border border-wle-red/20 rounded-lg">
                      <p className="text-sm font-medium text-wle-red">
                        ⚠️ WLE is not responsible for any results used directly for manufacturing or installation 
                        without proper engineering review and approval.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </TooltipProvider>
  );
};

export default TankWeightCalculator;